import { useCallback, useEffect, useMemo, useState } from "react";
import adminService from "../../api/services/adminService";
import { buildSubscriptionHistoryParams } from "./buildParams";
import { getFilterLabel as getFilterLabelUtil } from "./getFilterLabel";
import { INITIAL_FILTERS, LIMIT } from "./constants";

/**
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - If false, skips fetching (e.g. when History tab is not active).
 */
export function useSubscriptionHistory(options = {}) {
  const { enabled = true } = options;
  const [historyFilters, setHistoryFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    adminService
      .getSubscriptionPlans()
      .then((res) => {
        if (res?.success && res?.plans) setPlans(res.plans);
      })
      .catch(() => {});
  }, []);

  const fetchSubscriptionHistory = useCallback(async (page = 1, filters = {}) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const params = buildSubscriptionHistoryParams(filters, page, LIMIT);
      const response = await adminService.getSubscriptionHistory(params);
      if (response?.success && response?.history) {
        setSubscriptionHistory(response.history);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        }
      }
    } catch (err) {
      console.error("Error fetching subscription history:", err);
      setHistoryError(err?.message || "Failed to fetch subscription history");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchSubscriptionHistory(currentPage, historyFilters);
  }, [
    enabled,
    currentPage,
    historyFilters.user_id,
    historyFilters.name,
    historyFilters.email,
    historyFilters.contactNo,
    historyFilters.date_from,
    historyFilters.date_to,
    historyFilters.subscription,
    historyFilters.source,
    historyFilters.currency,
    fetchSubscriptionHistory,
  ]);

  const handleFilterChange = useCallback((key, value) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setHistoryFilters(INITIAL_FILTERS);
    setCurrentPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: "" }));
    setCurrentPage(1);
  }, []);

  const getFilterLabel = useCallback(
    (key, value) => getFilterLabelUtil(key, value),
    []
  );

  const getActiveFiltersCount = useCallback(() => {
    return Object.values(historyFilters).filter(
      (v) => v !== "" && v != null
    ).length;
  }, [historyFilters]);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(historyFilters).filter(
        (key) => historyFilters[key] !== "" && historyFilters[key] != null
      ),
    [historyFilters]
  );

  const primaryFields = useMemo(
    () => [
      {
        name: "user_id",
        label: "User ID",
        type: "number",
        placeholder: "Filter by user ID",
      },
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "Search by name",
      },
      {
        name: "email",
        label: "Email",
        type: "text",
        placeholder: "Search by email",
      },
      {
        name: "contactNo",
        label: "Contact No",
        type: "text",
        placeholder: "Search by contact",
      },
      { name: "date_from", label: "Date From", type: "date" },
      { name: "date_to", label: "Date To", type: "date" },
      {
        name: "subscription",
        label: "Subscription Type",
        type: "select",
        options: [
          { value: "", name: "All Types" },
          ...(plans || []).map((p) => ({
            value: String(p.id),
            name: p.name || `Plan ${p.id}`,
          })),
        ],
      },
      {
        name: "source",
        label: "Source",
        type: "select",
        options: [
          { value: "", name: "All Sources" },
          { value: "manual", name: "Manual" },
          { value: "revenuecat", name: "RevenueCat" },
        ],
      },
      {
        name: "currency",
        label: "Currency",
        type: "text",
        placeholder: "e.g. PKR, USD",
      },
    ],
    [plans]
  );

  return {
    historyFilters,
    setHistoryFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    subscriptionHistory,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    loadingHistory,
    historyError,
    limit: LIMIT,
    fetchSubscriptionHistory,
  };
}
